import { Box } from "@mui/material";
import { useCampaigns } from "api/campaigns";
import { getBackendErrors } from "api/errors";
import { useAppSelector } from "app/hooks";
import { StepperFormButtons } from "components/FormControls/StepperFormButtons";
import { getCurrentName } from "components/Layout/Achievement/AchievementImportList/utils";
import { Stepper } from "components/Stepper/Stepper";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { surveyQuery } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { useStepper } from "hooks/useStepper";
import { isEmpty } from "lodash";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import {
  CampaignFEValues,
  CampaignTriggers,
  CampaignTypes,
  DirectTransactionCampaign,
  VisibilityTarget,
} from "types/campaigns";
import { Status, StepDetails } from "types/stepper";
import {
  backendErrors,
  parseCampaignTranslationsArrayError,
} from "utilities/backendErrors";
import { checkIfRulesEditMode } from "utilities/checkRulesEditMode";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { formatSubmitValues } from "../../Common/campaignHelpers";
import { Info } from "./Info/Info";
import { Limitations } from "./Limitations/Limitations";
import { Rules } from "./Rules/Rules";
import { useStyles } from "./styles";
import { Summary } from "./Summary/Summary";
import { Triggers } from "./Triggers/Triggers";
import { getTabWithErrors } from "./utils";
import {
  checkTranslationsError,
  getValidationSchemaByStep,
  hasRuleErrors,
  updateErrors,
} from "./validationSchema";

interface Props {
  initialValues?: CampaignFEValues;
  campaignId?: string;
  isEditMode?: boolean;
  isDuplicationMode?: boolean;
  onSaveCallback?: (campaign: any) => void;
  initialErrors?: FormikErrors<CampaignFEValues>;
  onCancelCallback?: () => void;
}

export enum CampaignStepsIds {
  TRIGGER = "trigger",
  INFO = "info",
  RULES = "rules",
  LIMITAION = "limitation",
  SUMMARY = "summary",
}

export type CampaignStepsUnion = `${CampaignStepsIds}`;

export const campaignFormSteps: StepDetails<CampaignStepsUnion>[] = [
  {
    id: CampaignStepsIds.TRIGGER,
    label: "campaignTrigger",
    description: "selectEventThatStartsCampaign",
    status: Status.ACTIVE,
  },
  {
    id: CampaignStepsIds.INFO,
    label: "campaignInfo",
    description: "basicSettings",
    status: Status.NOT_VISITED,
  },
  {
    id: CampaignStepsIds.RULES,
    label: "rules&effects",
    description: "defineRules&effectDesc",
    status: Status.NOT_VISITED,
  },
  {
    id: CampaignStepsIds.LIMITAION,
    label: "limmitation&budget",
    description: "defineLimitation",
    status: Status.NOT_VISITED,
  },
  {
    id: CampaignStepsIds.SUMMARY,
    label: "summary",
    description: "reviewConfiguration",
    status: Status.NOT_VISITED,
  },
];

const CampaignForm = ({
  initialValues,
  isEditMode = false,
  campaignId,
  isDuplicationMode = false,
  onSaveCallback,
  initialErrors,
  onCancelCallback,
}: Props) => {
  const { message } = useMessage();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { addCampaign, editCampaign } = useCampaigns();
  const classes = useStyles();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const {
    steps,
    isLastStep,
    activeStep,
    setSteps,
    nextStep,
    prevStep,
    setStepsErrors,
    setStepError,
    skipToStep,
  } = useStepper(campaignFormSteps);

  useEffect(() => {
    if (initialErrors) {
      const tabWithErrors = getTabWithErrors(initialErrors);
      setStepsErrors(tabWithErrors);
    }
    // eslint-disable-next-line
  }, [initialErrors]);

  const handleSubmit = async (
    values: CampaignFEValues,
    { setErrors }: FormikHelpers<any>,
  ) => {
    const areRulesInEditMode = checkIfRulesEditMode();
    if (areRulesInEditMode) {
      return focusError();
    }

    const properties = formatSubmitValues({
      values,
    });
    try {
      const response =
        isEditMode && campaignId && !isDuplicationMode
          ? await editCampaign(campaignId, properties)
          : await addCampaign(properties);

      onSaveCallback
        ? onSaveCallback({
            campaignId: (response as { data: { campaignId: string } }).data
              .campaignId,
            name: defaultLang?.code
              ? getCurrentName(
                  properties?.translations as {
                    [key: string]: {
                      description?: string;
                      name: string;
                    };
                  },
                  defaultLang.code,
                )
              : "",
            ...properties,
          })
        : navigate(
            isEditMode
              ? `${links.campaignsList}`
              : `${links.campaignsList}${surveyQuery}`,
          );

      notify(
        message({
          id:
            isEditMode && !isDuplicationMode
              ? "campaignUpdated"
              : "campaignCreated",
        }),
      );
    } catch (exception) {
      const errors = backendErrors(getBackendErrors(exception));

      const parsedErrors = {
        ...errors,
        ...(errors?.translations
          ? {
              translations: parseCampaignTranslationsArrayError(
                errors?.translations,
                values?.translations,
              ),
            }
          : {}),
      };
      setErrors(parsedErrors);

      focusError();
      const tabWithErrors = getTabWithErrors(parsedErrors);
      setStepsErrors(tabWithErrors);
    }
  };

  const defaultValues: DirectTransactionCampaign = {
    type: CampaignTypes.DIRECT,
    trigger: CampaignTriggers.TRANSACTION,
    translations: [
      defaultLang && {
        description: "",
        locale: defaultLang.code,
        name: "",
      },
    ],
    activity: {
      startsAt: null,
      endsAt: null,
    },
    transactionItemsFilters: [],
    rules: [],
    labels: [],
    limits: {
      executions: { interval: { type: "noLimit" } },
      executionsPerMember: { interval: { type: "noLimit" } },
      points: { interval: { type: "noLimit" } },
      pointsPerMember: { interval: { type: "noLimit" } },
    },
    active: false,
    visibility: { target: VisibilityTarget.ALL },
  };
  const nextStepInfo = isLastStep
    ? activeStep
    : steps[steps.findIndex((step) => step.id === activeStep.id) + 1];

  const validationSchema = getValidationSchemaByStep(
    activeStep.id,
    message,
    isEditMode,
  );

  return (
    <Formik
      initialValues={initialValues || defaultValues}
      onSubmit={handleSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize
      initialErrors={initialErrors}
      validationSchema={validationSchema}
    >
      {({
        handleSubmit,
        values,
        setValues,
        errors,
        isSubmitting,
        dirty,
        validateForm,
        setErrors,
        initialValues,
      }) => {
        const { trigger, translations, limits, labels, rules } = values;
        const event = "event" in values ? values.event : undefined;
        const achievementId =
          "achievementId" in values ? values.achievementId : undefined;

        const validateStep = async () => {
          if (activeStep.id === CampaignStepsIds.RULES) {
            const areRulesInEditMode = checkIfRulesEditMode();

            if (areRulesInEditMode) {
              setErrors({
                rules: message({
                  id: "toSaveCampaignFinishEditingRules",
                }),
              });
              return focusError();
            }

            const hasError = hasRuleErrors(errors);

            if (hasError) return false;
          }

          const stepErrors = await validateForm();
          const currentStepFields = Object.keys(validationSchema?.fields || {});
          const areStepErrors = Object.keys(stepErrors).length > 0;
          const mergedErrors = updateErrors(
            errors,
            stepErrors,
            currentStepFields,
          );

          setErrors(mergedErrors);

          if (areStepErrors) return false;

          setStepError(activeStep.id, false);

          return true;
        };

        return (
          <>
            <div className={classes.content}>
              <Box className={classes.stepper}>
                <Box className={classes.contentWrapper}>
                  <Stepper
                    steps={steps}
                    setSteps={async (
                      stepsDetails: StepDetails<CampaignStepsUnion>[],
                      isPreviousStepClicked,
                    ) => {
                      const isStepValid = await validateStep();

                      return setSteps(
                        stepsDetails.map((step) => ({
                          ...step,
                          hasError:
                            step.id === activeStep.id
                              ? !isStepValid
                              : step.hasError,
                        })),
                      );
                    }}
                  />
                </Box>
                <div className={classes.separator} />
              </Box>
              <Form
                onSubmit={handleSubmit}
                className={`${classes.contentWrapper} ${classes.form}`}
              >
                {activeStep.id === CampaignStepsIds.TRIGGER && (
                  <Triggers
                    trigger={trigger}
                    event={event}
                    achievementId={achievementId}
                    setFieldsValue={(newValues: any) =>
                      setValues({
                        ...values,
                        ...newValues,
                      })
                    }
                    isEditMode={isEditMode}
                    errors={errors}
                    defaultLang={defaultLang}
                  />
                )}

                {activeStep.id === CampaignStepsIds.INFO && (
                  <Info
                    availableLanguages={availableLanguages}
                    translations={translations}
                    labels={labels}
                    sectionErrors={{
                      title: checkTranslationsError(errors?.translations),
                      startEndDate: !isEmpty(errors?.activity),
                      visibility: !isEmpty(errors?.visibility),
                      attributes: !isEmpty(errors?.labels),
                    }}
                  />
                )}

                {activeStep.id === CampaignStepsIds.RULES && (
                  <Rules
                    rules={rules}
                    trigger={trigger}
                    event={event}
                    transactionItemsFilters={
                      trigger === CampaignTriggers.TRANSACTION ||
                      trigger === CampaignTriggers.RETURN_TRANSACTION
                        ? values.transactionItemsFilters
                        : undefined
                    }
                    errors={errors}
                    isEditMode={isEditMode}
                  />
                )}

                {activeStep.id === CampaignStepsIds.LIMITAION && (
                  <Limitations
                    trigger={trigger}
                    limits={limits}
                    sectionErrors={{
                      limit: !isEmpty(errors?.limits?.executionsPerMember),
                      budget: !isEmpty(
                        errors.limits?.points ||
                          errors?.limits?.pointsPerMember,
                      ),
                    }}
                  />
                )}

                {activeStep.id === CampaignStepsIds.SUMMARY && (
                  <Summary
                    values={values}
                    defaultLang={defaultLang}
                    skipToStep={skipToStep}
                    errors={errors}
                  />
                )}

                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
              </Form>
            </div>
            <StepperFormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => {
                if (onCancelCallback) {
                  return onCancelCallback();
                }
                return navigate(links.campaignsList);
              }}
              onPrevStepClick={async () => {
                const isStepValid = await validateStep();
                if (!isStepValid) setStepError(activeStep.id, true);
                prevStep();
              }}
              onSubmitClick={async () => {
                if (isLastStep) handleSubmit();
                const isStepValid = await validateStep();

                if (!isStepValid) {
                  setStepError(activeStep.id, true);
                }

                nextStep();
              }}
              submitMessage={
                !isLastStep
                  ? `${message({ id: "nextStep" })}: ${message({ id: nextStepInfo?.label })}`
                  : isEditMode
                    ? "saveChanges"
                    : "addCampaign"
              }
            />
          </>
        );
      }}
    </Formik>
  );
};

export default CampaignForm;
